/* bs-header — custom element <bs-header> con 4 behaviors:
   1. Drawer mobile (toggle + focus trap + escape + overlay click + body inert)
   2. Megamenu desktop (hover debounce + click fallback + escape + click outside)
   3. Sticky header (IntersectionObserver sobre sentinel)
   4. Predictive search (fetch /search/suggest.json + keyboard nav + click outside)

   Patrón: custom element con guard contra doble init via flag interno.
   Aislado en IIFE — no contamina scope global. */

(function () {
  'use strict';

  if (window.customElements && window.customElements.get('bs-header')) {
    // Ya registrado — Customizer recargó el script. No re-registrar (throw).
    return;
  }

  var REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';
  var FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  var SEARCH_DEBOUNCE_MS = 200;
  var MEGAMENU_OPEN_MS = 100;
  var MEGAMENU_CLOSE_MS = 200;

  function debounce(fn, ms) {
    var t = null;
    return function () {
      var ctx = this;
      var args = arguments;
      if (t) clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, ms);
    };
  }

  function prefersReducedMotion() {
    return window.matchMedia(REDUCED_MOTION_QUERY).matches;
  }

  // ===== Badge del carrito: sincronizar tras add-to-cart AJAX (evento 'cart:update' de Horizon).
  //  bs-header es custom y no usa el cart-icon de Horizon → el badge {{ cart.item_count }} quedaba
  //  estático hasta recargar. Este listener (la IIFE solo corre en el primer load, se registra 1 vez)
  //  actualiza .bs-header__cart-badge cuando el hero / FBT / cualquier section disparan cart:update.
  function bsUpdateCartBadge(count) {
    if (typeof count !== 'number' || count < 0) return;
    var badges = document.querySelectorAll('.bs-header__cart-badge');
    for (var i = 0; i < badges.length; i++) {
      badges[i].textContent = count;
      if (count > 0) { badges[i].removeAttribute('hidden'); } else { badges[i].setAttribute('hidden', ''); }
    }
    var links = document.querySelectorAll('.bs-header__cart');
    var word = count === 1 ? ' artículo' : ' artículos';
    for (var j = 0; j < links.length; j++) {
      links[j].setAttribute('aria-label', 'Carrito, ' + count + word);
    }
  }
  document.addEventListener('cart:update', function (e) {
    var c = e && e.detail && e.detail.resource && e.detail.resource.item_count;
    if (typeof c === 'number') {
      bsUpdateCartBadge(c);
    } else {
      fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (cart) { bsUpdateCartBadge(cart.item_count); })
        .catch(function () {});
    }
  });

  // ============================================================
  // Custom element
  // ============================================================
  class BsHeader extends HTMLElement {
    constructor() {
      super();
      this._initialized = false;
      this._cleanup = [];
    }

    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;

      this.setupDrawer();
      this.setupMegamenu();
      this.setupSticky();
      this.setupPredictiveSearch();
      this.setupVoiceSearch();
    }

    disconnectedCallback() {
      // Limpieza: ejecutar todos los teardowns registrados.
      for (var i = 0; i < this._cleanup.length; i++) {
        try { this._cleanup[i](); } catch (e) { /* noop */ }
      }
      this._cleanup = [];
      this._initialized = false;
    }

    _addCleanup(fn) {
      this._cleanup.push(fn);
    }

    // ============================================================
    // 1. DRAWER mobile
    // ============================================================
    setupDrawer() {
      var host = this;
      var toggle = this.querySelector('.bs-header__menu-toggle');
      var drawer = this.querySelector('[data-bs-header-drawer]');
      var overlay = this.querySelector('[data-bs-header-drawer-overlay]');
      var closeBtn = this.querySelector('[data-bs-header-drawer-close]');
      var panel = drawer ? drawer.querySelector('.bs-header__drawer-panel') : null;

      if (!toggle || !drawer || !panel) return;

      var lastFocused = null;

      function open() {
        if (host.classList.contains('bs-header--drawer-open')) return;
        lastFocused = document.activeElement;
        host.classList.add('bs-header--drawer-open');
        toggle.setAttribute('aria-expanded', 'true');
        drawer.setAttribute('aria-hidden', 'false');
        drawer.removeAttribute('inert');
        // Bloquear scroll del body sin reflow brusco.
        document.body.style.overflow = 'hidden';
        // Mover foco al primer focusable del panel.
        requestAnimationFrame(function () {
          var first = panel.querySelector(FOCUSABLE_SELECTOR);
          if (first) first.focus();
        });
      }

      function close() {
        if (!host.classList.contains('bs-header--drawer-open')) return;
        host.classList.remove('bs-header--drawer-open');
        toggle.setAttribute('aria-expanded', 'false');
        drawer.setAttribute('aria-hidden', 'true');
        drawer.setAttribute('inert', '');
        document.body.style.overflow = '';
        // Restaurar foco al toggle (o al elemento previo si existe).
        var target = lastFocused && lastFocused.focus ? lastFocused : toggle;
        try { target.focus(); } catch (e) { toggle.focus(); }
      }

      function onToggleClick(e) {
        e.preventDefault();
        if (host.classList.contains('bs-header--drawer-open')) close();
        else open();
      }

      function onOverlayClick() {
        close();
      }

      function onCloseClick(e) {
        e.preventDefault();
        close();
      }

      function onKeydown(e) {
        if (!host.classList.contains('bs-header--drawer-open')) return;
        if (e.key === 'Escape' || e.key === 'Esc') {
          e.preventDefault();
          close();
          return;
        }
        // Focus trap: si Tab fuera del panel, traer de vuelta.
        if (e.key === 'Tab') {
          var focusables = panel.querySelectorAll(FOCUSABLE_SELECTOR);
          if (focusables.length === 0) return;
          var first = focusables[0];
          var last = focusables[focusables.length - 1];
          if (e.shiftKey && document.activeElement === first) {
            e.preventDefault();
            last.focus();
          } else if (!e.shiftKey && document.activeElement === last) {
            e.preventDefault();
            first.focus();
          }
        }
      }

      toggle.addEventListener('click', onToggleClick);
      if (overlay) overlay.addEventListener('click', onOverlayClick);
      if (closeBtn) closeBtn.addEventListener('click', onCloseClick);
      // Keydown global porque el foco puede estar en cualquier focusable del panel.
      document.addEventListener('keydown', onKeydown);

      this._addCleanup(function () {
        toggle.removeEventListener('click', onToggleClick);
        if (overlay) overlay.removeEventListener('click', onOverlayClick);
        if (closeBtn) closeBtn.removeEventListener('click', onCloseClick);
        document.removeEventListener('keydown', onKeydown);
        document.body.style.overflow = '';
      });

      // Acordeón nested dentro del drawer.
      var toggles = this.querySelectorAll('[data-bs-header-drawer-toggle]');
      for (var i = 0; i < toggles.length; i++) {
        (function (btn) {
          var subnavId = btn.getAttribute('aria-controls');
          var subnav = subnavId ? document.getElementById(subnavId) : null;
          if (!subnav) return;

          function onAccordionClick(e) {
            e.preventDefault();
            var isOpen = btn.getAttribute('aria-expanded') === 'true';
            if (isOpen) {
              btn.setAttribute('aria-expanded', 'false');
              subnav.setAttribute('hidden', '');
            } else {
              btn.setAttribute('aria-expanded', 'true');
              subnav.removeAttribute('hidden');
            }
          }
          btn.addEventListener('click', onAccordionClick);
          host._addCleanup(function () {
            btn.removeEventListener('click', onAccordionClick);
          });
        })(toggles[i]);
      }
    }

    // ============================================================
    // 2. MEGAMENU desktop
    // ============================================================
    setupMegamenu() {
      var host = this;
      var navItems = this.querySelectorAll('.bs-header__nav-li--has-megamenu');
      if (navItems.length === 0) return;

      var openTimer = null;
      var closeTimer = null;
      var currentOpen = null;

      function openMegamenu(li) {
        if (closeTimer) { clearTimeout(closeTimer); closeTimer = null; }
        if (currentOpen && currentOpen !== li) {
          closeMegamenuImmediate(currentOpen);
        }
        var trigger = li.querySelector('[data-has-megamenu]');
        var panel = li.querySelector('[data-bs-header-megamenu]');
        if (!trigger || !panel) return;
        trigger.setAttribute('aria-expanded', 'true');
        panel.removeAttribute('hidden');
        li.classList.add('bs-header__nav-li--open');
        currentOpen = li;
      }

      function closeMegamenuImmediate(li) {
        var trigger = li.querySelector('[data-has-megamenu]');
        var panel = li.querySelector('[data-bs-header-megamenu]');
        if (!trigger || !panel) return;
        trigger.setAttribute('aria-expanded', 'false');
        panel.setAttribute('hidden', '');
        li.classList.remove('bs-header__nav-li--open');
        if (currentOpen === li) currentOpen = null;
      }

      function scheduleOpen(li) {
        if (openTimer) clearTimeout(openTimer);
        if (closeTimer) { clearTimeout(closeTimer); closeTimer = null; }
        openTimer = setTimeout(function () { openMegamenu(li); }, MEGAMENU_OPEN_MS);
      }

      function scheduleClose(li) {
        if (openTimer) { clearTimeout(openTimer); openTimer = null; }
        if (closeTimer) clearTimeout(closeTimer);
        closeTimer = setTimeout(function () { closeMegamenuImmediate(li); }, MEGAMENU_CLOSE_MS);
      }

      for (var i = 0; i < navItems.length; i++) {
        (function (li) {
          var trigger = li.querySelector('[data-has-megamenu]');
          var panel = li.querySelector('[data-bs-header-megamenu]');
          if (!trigger || !panel) return;

          function onEnter() { scheduleOpen(li); }
          function onLeave() { scheduleClose(li); }
          function onClick(e) {
            // Click fallback (keyboard / touch): toggle del panel.
            if (li.classList.contains('bs-header__nav-li--open')) {
              e.preventDefault();
              closeMegamenuImmediate(li);
            } else {
              e.preventDefault();
              openMegamenu(li);
            }
          }
          function onFocus() { openMegamenu(li); }

          li.addEventListener('mouseenter', onEnter);
          li.addEventListener('mouseleave', onLeave);
          panel.addEventListener('mouseenter', function () {
            if (closeTimer) { clearTimeout(closeTimer); closeTimer = null; }
          });
          panel.addEventListener('mouseleave', onLeave);
          trigger.addEventListener('click', onClick);
          trigger.addEventListener('focus', onFocus);

          host._addCleanup(function () {
            li.removeEventListener('mouseenter', onEnter);
            li.removeEventListener('mouseleave', onLeave);
            panel.removeEventListener('mouseleave', onLeave);
            trigger.removeEventListener('click', onClick);
            trigger.removeEventListener('focus', onFocus);
          });
        })(navItems[i]);
      }

      // Escape cierra megamenu abierto.
      function onKeydown(e) {
        if (e.key === 'Escape' || e.key === 'Esc') {
          if (currentOpen) {
            var trigger = currentOpen.querySelector('[data-has-megamenu]');
            closeMegamenuImmediate(currentOpen);
            if (trigger) trigger.focus();
          }
        }
      }
      // Click outside cierra megamenu abierto.
      function onDocClick(e) {
        if (!currentOpen) return;
        if (currentOpen.contains(e.target)) return;
        closeMegamenuImmediate(currentOpen);
      }
      document.addEventListener('keydown', onKeydown);
      document.addEventListener('click', onDocClick);
      this._addCleanup(function () {
        document.removeEventListener('keydown', onKeydown);
        document.removeEventListener('click', onDocClick);
        if (openTimer) clearTimeout(openTimer);
        if (closeTimer) clearTimeout(closeTimer);
      });
    }

    // ============================================================
    // 3. STICKY header (IntersectionObserver sobre sentinel)
    // ============================================================
    setupSticky() {
      if (this.getAttribute('data-sticky') !== 'true') return;
      if (typeof IntersectionObserver === 'undefined') return;

      var host = this;
      var sentinel = this.querySelector('.bs-header__sticky-sentinel');
      if (!sentinel) return;

      var observer = new IntersectionObserver(function (entries) {
        for (var i = 0; i < entries.length; i++) {
          if (entries[i].isIntersecting) {
            host.classList.remove('bs-header--scrolled');
          } else {
            host.classList.add('bs-header--scrolled');
          }
        }
      }, { threshold: 0, rootMargin: '0px' });

      observer.observe(sentinel);
      this._addCleanup(function () {
        observer.disconnect();
      });
    }

    // ============================================================
    // 4. PREDICTIVE SEARCH (fetch /search/suggest.json)
    // ============================================================
    setupPredictiveSearch() {
      var host = this;
      var forms = this.querySelectorAll('[data-bs-header-search]');
      if (forms.length === 0) return;

      var lastRequestController = null;

      function fetchSuggestions(query, resultsEl, formEl) {
        // Abortar request anterior (race conditions).
        if (lastRequestController) {
          try { lastRequestController.abort(); } catch (e) { /* noop */ }
        }
        if (typeof AbortController !== 'undefined') {
          lastRequestController = new AbortController();
        }

        // Fetch HTML pre-renderizado por Liquid en la section bs-predictive-search.
        // Patrón Shopify: /search/suggest?q=X&section_id=<id> devuelve la section
        // con el objeto `predictive_search` populado. limit_scope=each da N de cada
        // tipo en vez de N total mezclados.
        var url = '/search/suggest?q=' + encodeURIComponent(query)
          + '&section_id=bs-predictive-search'
          + '&resources[type]=product,collection,query,page,article'
          + '&resources[limit]=4'
          + '&resources[limit_scope]=each';

        var opts = lastRequestController ? { signal: lastRequestController.signal } : {};

        fetch(url, opts)
          .then(function (r) { return r.text(); })
          .then(function (html) {
            renderResults(html, resultsEl);
          })
          .catch(function (err) {
            if (err && err.name === 'AbortError') return;
            // Silenciar errores transient — no romper la UX.
          });
      }

      function renderResults(html, resultsEl) {
        // La response viene con un wrapper <div class="shopify-section">. Extraemos
        // solo el contenido interno (.bs-predictive-search) y lo inyectamos.
        var parser = new DOMParser();
        var doc = parser.parseFromString(html, 'text/html');
        var inner = doc.querySelector('[data-bs-predictive-content]');
        resultsEl.innerHTML = inner ? inner.outerHTML : html;
        resultsEl.removeAttribute('hidden');
        setSearchExpanded(resultsEl, true);
      }

      function clearResults(resultsEl) {
        if (!resultsEl) return;
        resultsEl.innerHTML = '';
        resultsEl.setAttribute('hidden', '');
        setSearchExpanded(resultsEl, false);
      }

      function setSearchExpanded(resultsEl, expanded) {
        if (!resultsEl || !resultsEl.id) return;
        var input = host.querySelector('[aria-controls="' + resultsEl.id + '"]');
        if (input) input.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      }

      function setupForm(form) {
        var input = form.querySelector('[data-bs-header-search-input]');
        var resultsEl = form.querySelector('[data-bs-header-search-results]');
        if (!input || !resultsEl) return;

        var debounced = debounce(function (value) {
          if (!value || value.length < 2) {
            clearResults(resultsEl);
            return;
          }
          fetchSuggestions(value, resultsEl, form);
        }, SEARCH_DEBOUNCE_MS);

        function onInput() {
          debounced(input.value.trim());
        }
        function onFocus() {
          if (input.value.trim().length >= 2) {
            fetchSuggestions(input.value.trim(), resultsEl, form);
          }
        }
        function onKeydown(e) {
          // role="option" matchea pills (queries), resource cards y "ver todos"
          // en el nuevo markup renderizado por bs-predictive-search.liquid.
          var items = resultsEl.querySelectorAll('[role="option"]');
          var selected = resultsEl.querySelector('[aria-selected="true"]');
          var idx = -1;
          if (selected) {
            for (var i = 0; i < items.length; i++) {
              if (items[i] === selected) { idx = i; break; }
            }
          }

          if (e.key === 'ArrowDown') {
            e.preventDefault();
            if (items.length === 0) return;
            if (selected) selected.removeAttribute('aria-selected');
            var next = items[Math.min(idx + 1, items.length - 1)];
            if (next) next.setAttribute('aria-selected', 'true');
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            if (items.length === 0) return;
            if (selected) selected.removeAttribute('aria-selected');
            var prev = items[Math.max(idx - 1, 0)];
            if (prev) prev.setAttribute('aria-selected', 'true');
          } else if (e.key === 'Enter') {
            if (selected) {
              e.preventDefault();
              window.location.href = selected.getAttribute('href');
            }
            // Sino, deja que el form haga submit nativo.
          } else if (e.key === 'Escape' || e.key === 'Esc') {
            clearResults(resultsEl);
          }
        }

        input.addEventListener('input', onInput);
        input.addEventListener('focus', onFocus);
        input.addEventListener('keydown', onKeydown);

        host._addCleanup(function () {
          input.removeEventListener('input', onInput);
          input.removeEventListener('focus', onFocus);
          input.removeEventListener('keydown', onKeydown);
        });
      }

      for (var i = 0; i < forms.length; i++) {
        setupForm(forms[i]);
      }

      // Click outside cierra todos los dropdowns.
      function onDocClick(e) {
        for (var i = 0; i < forms.length; i++) {
          if (!forms[i].contains(e.target)) {
            var r = forms[i].querySelector('[data-bs-header-search-results]');
            clearResults(r);
          }
        }
      }
      document.addEventListener('click', onDocClick);
      this._addCleanup(function () {
        document.removeEventListener('click', onDocClick);
      });
    }

    // ============== Voice search (Web Speech API) ==============
    // Browser support: Chrome/Edge/Safari (desktop+iOS). Firefox no soporta SpeechRecognition.
    // Si no hay soporte, ocultamos los botones del mic para no exponer una UI rota.
    setupVoiceSearch() {
      var host = this;
      var SpeechRecognitionImpl = window.SpeechRecognition || window.webkitSpeechRecognition;
      var micButtons = host.querySelectorAll('.bs-header__search-mic');

      if (!SpeechRecognitionImpl) {
        micButtons.forEach(function (btn) { btn.style.display = 'none'; });
        return;
      }

      // Idioma: usar `<html lang>` (Shopify lo setea según Markets). Fallback es-ES.
      var lang = (document.documentElement.lang || 'es-ES').replace('_', '-');
      var recogLang = lang.toLowerCase().indexOf('es') === 0 ? 'es-ES' : lang;

      micButtons.forEach(function (btn) {
        var form = btn.closest('form');
        var input = form ? form.querySelector('[data-bs-header-search-input]') : null;
        if (!input) return;

        var recognition = null;
        var originalPlaceholder = input.placeholder;
        var listening = false;

        function cleanup() {
          listening = false;
          btn.classList.remove('bs-header__search-mic--listening');
          btn.setAttribute('aria-label', 'Búsqueda por voz');
          input.placeholder = originalPlaceholder;
        }

        function showError(msg, ms) {
          input.placeholder = msg;
          setTimeout(function () { input.placeholder = originalPlaceholder; }, ms || 4000);
        }

        function startRecognition() {
          try {
            recognition = new SpeechRecognitionImpl();
          } catch (err) {
            console.warn('[bs-header] SpeechRecognition no disponible:', err);
            return;
          }

          recognition.lang = recogLang;
          recognition.continuous = false;
          recognition.interimResults = false;
          recognition.maxAlternatives = 1;

          recognition.onstart = function () {
            listening = true;
            btn.classList.add('bs-header__search-mic--listening');
            btn.setAttribute('aria-label', 'Escuchando, pulsa para parar');
            input.placeholder = 'Habla ahora…';
            input.focus();
          };

          recognition.onresult = function (event) {
            var transcript = (event.results[0] && event.results[0][0] && event.results[0][0].transcript) || '';
            if (transcript) {
              input.value = transcript;
              input.dispatchEvent(new Event('input', { bubbles: true }));
            }
          };

          recognition.onerror = function (event) {
            if (event.error === 'not-allowed' || event.error === 'service-not-allowed') {
              showError('Permite el micrófono en tu navegador');
            } else if (event.error === 'audio-capture') {
              showError('No se detecta micrófono en tu dispositivo');
            } else if (event.error === 'network') {
              showError('Sin conexión para reconocer voz');
            } else if (event.error !== 'no-speech' && event.error !== 'aborted') {
              console.warn('[bs-header] Voice recognition error:', event.error);
            }
            cleanup();
          };

          recognition.onend = cleanup;

          try {
            recognition.start();
          } catch (err) {
            cleanup();
            console.warn('[bs-header] No se pudo iniciar voice recognition:', err);
          }
        }

        function onClick(e) {
          e.preventDefault();

          // Toggle: si ya está escuchando, cancelar.
          if (listening && recognition) {
            try { recognition.stop(); } catch (err) {}
            return;
          }

          // Forzar prompt de micrófono visible + obtener permiso PERSISTENTE.
          // Sin este paso, SpeechRecognition.start() pide permiso internamente
          // pero el prompt se desvanece muy rápido y muchos browsers no lo
          // muestran de forma clara — el usuario nota un parpadeo y "no funciona".
          // getUserMedia es síncrono UX-wise: el prompt queda visible hasta que
          // el usuario decide. Tras grant, cerramos el stream (SpeechRecognition
          // gestiona su propio audio capture internamente).
          if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
            startRecognition();
            return;
          }

          navigator.mediaDevices.getUserMedia({ audio: true }).then(function (stream) {
            stream.getTracks().forEach(function (t) { t.stop(); });
            startRecognition();
          }).catch(function (err) {
            if (err.name === 'NotAllowedError' || err.name === 'SecurityError') {
              showError('Permite el micrófono en tu navegador');
            } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
              showError('No se detecta micrófono en tu dispositivo');
            } else {
              console.warn('[bs-header] getUserMedia error:', err.name, err.message);
              showError('No se pudo acceder al micrófono');
            }
          });
        }

        btn.addEventListener('click', onClick);
        host._addCleanup(function () { btn.removeEventListener('click', onClick); });
      });
    }
  }

  customElements.define('bs-header', BsHeader);

  // Re-init en el Customizer: el browser ya hace upgrade del custom element al
  // insertarse en el DOM, pero forzamos cleanup→reinit por si el host element
  // se reutiliza con cambios internos.
  document.addEventListener('shopify:section:load', function (e) {
    var host = e.target.querySelector ? e.target.querySelector('bs-header') : null;
    if (host && typeof host.connectedCallback === 'function') {
      // Si ya está inicializado, forzar re-run.
      host._initialized = false;
      host.connectedCallback();
    }
  });

  document.addEventListener('shopify:section:unload', function (e) {
    var host = e.target.querySelector ? e.target.querySelector('bs-header') : null;
    if (host && typeof host.disconnectedCallback === 'function') {
      host.disconnectedCallback();
    }
  });

  // ============================================================
  // WISHLIST COUNTER — sincroniza badge del header con el cache del plugin
  // ============================================================
  // Wishlist nativo (localStorage, sin plugin). La key 'bs_wishlist' la gestiona
  // bs-wishlist.js (asset global cargado en el header) con shape [{h,v,p}].
  // bs-wishlist.js dispara CustomEvent bs:wishlist:change (con detail.count) tras
  // cada toggle — aquí solo leemos al cargar y escuchamos el evento.
  var WISHLIST_STORAGE_KEY = 'bs_wishlist';

  function readWishlistCount() {
    try {
      var raw = localStorage.getItem(WISHLIST_STORAGE_KEY);
      if (!raw) return 0;
      var parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed.length : 0;
    } catch (err) {
      return 0;
    }
  }

  function updateWishlistBadge(count) {
    var badges = document.querySelectorAll('[data-bs-header-wishlist-count]');
    badges.forEach(function (badge) {
      badge.textContent = String(count);
      if (count > 0) {
        badge.removeAttribute('hidden');
      } else {
        badge.setAttribute('hidden', '');
      }
    });
  }

  // Init: estado actual desde localStorage al cargar la pagina
  updateWishlistBadge(readWishlistCount());

  // Listener: cada toggle desde una card del carousel emite este event con
  // el nuevo count en el detail (mas eficiente que re-leer localStorage).
  document.addEventListener('bs:wishlist:change', function (e) {
    if (e && e.detail && typeof e.detail.count === 'number') {
      updateWishlistBadge(e.detail.count);
    } else {
      updateWishlistBadge(readWishlistCount());
    }
  });

  // Sync entre pestañas: cuando el localStorage cambia en OTRA pestaña del
  // mismo origen, el browser dispara 'storage' aqui. Util si el usuario tiene
  // 2 pestañas de la tienda abiertas y añade un favorito en una.
  window.addEventListener('storage', function (e) {
    if (e.key === WISHLIST_STORAGE_KEY) {
      updateWishlistBadge(readWishlistCount());
    }
  });

  // Re-sync tras recargas de section en customizer
  document.addEventListener('shopify:section:load', function () {
    updateWishlistBadge(readWishlistCount());
  });
})();
